import { format, isValid, parse } from 'date-fns';

import { apolloClient } from 'core/apollo-client';
import { GET_AGENCY_REPORT_DOWNLOAD_URL } from './agency-reports.queries';

/** `2026-09` (the report's folder month) → `September 2026`; anything else is shown as-is. */
export const formatReportMonth = (reportMonth) => {
  if (!/^\d{4}-\d{2}$/.test(reportMonth ?? '')) {
    return reportMonth ?? '';
  }
  const month = parse(reportMonth, 'yyyy-MM', new Date());
  return isValid(month) ? format(month, 'MMMM yyyy') : reportMonth;
};

/**
 * Opens a report in a new tab. The lambda re-checks the key against the caller's scope and mints a
 * short-lived signed URL (#36219) — the same server-side-authorised pattern as document-download.js,
 * so no storage credential ever reaches the browser.
 *
 * The tab is reserved synchronously, inside the click's user activation, and navigated once the
 * (uncached, possibly slow) signing request returns: a `window.open` after the await is silently
 * popup-blocked once that activation expires, and the caller would never know. `opener` is cleared
 * on the reserved tab so the PDF host can't reach back into Staff (the equivalent of `noopener`).
 *
 * If the browser refuses even the reservation (strict pop-up blocking), that is reported at once
 * and nothing is signed. There is deliberately no second `window.open(url, …, 'noopener')` attempt:
 * with `noopener` the browser returns null whether or not it opened the tab, so its result can't
 * distinguish success from blocking — a retry would open the report AND show the blocked message.
 * Throws the API's message on the error union so the list can show it inline.
 */
export const openAgencyReport = async (key) => {
  const reservedTab = window.open('about:blank', '_blank');
  if (!reservedTab) {
    throw new Error('Your browser blocked the report pop-up. Allow pop-ups for this site and try again.');
  }
  reservedTab.opener = null;

  let url;
  try {
    const { data } = await apolloClient.query({
      query: GET_AGENCY_REPORT_DOWNLOAD_URL,
      variables: { key },
      fetchPolicy: 'no-cache'
    });
    const result = data?.getAgencyReportDownloadUrl;
    if (!result?.success || !result.url) {
      throw new Error(result?.error || 'Could not open this report. Please try again.');
    }
    url = result.url;
  } catch (failure) {
    reservedTab.close();
    throw failure;
  }

  // The user may have closed the blank tab while signing was in flight.
  if (!reservedTab.closed) {
    reservedTab.location.replace(url);
  }
};
